/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-explicit-any */
import { createFileRoute, useParams } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  Hash, Tag, CheckCircle2, FileText, ExternalLink,
  CreditCard, Clock, Building2, User, Phone, Mail,
  MapPin, AlertTriangle, Users, Wrench, ChevronRight, Briefcase, Copy,
} from 'lucide-react'
import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'
import { jobsAPI } from '@/lib/api/jobs'
import { cn } from '@/lib/utils'
import { useTranslation } from 'react-i18next'

function StatusBadge({ title, color }: { title?: string; color?: string | null }) {
  const { t } = useTranslation()
  if (!title) return <Badge variant="outline">{t('jobDetail:unknown')}</Badge>
  const bg = color ? { backgroundColor: color, color: '#fff' } : undefined
  return <Badge style={bg} variant={bg ? 'default' : 'secondary'}>{title}</Badge>
}

function formatDateTime(d: string | null | undefined) {
  if (!d) return '—'
  return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function InfoRow({ icon: Icon, label, children, className }: {
  icon: React.ElementType; label: string; children: React.ReactNode; className?: string
}) {
  return (
    <div className={cn('flex items-start gap-3 py-2', className)}>
      <Icon className="w-4 h-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-xs text-muted-foreground">{label}</p>
        <div className="text-sm">{children}</div>
      </div>
    </div>
  )
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            className="ml-1.5 text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500) }}
          >
            {copied ? <CheckCircle2 className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
          </button>
        </TooltipTrigger>
        <TooltipContent>{copied ? 'Copied' : 'Copy'}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

export const Route = createFileRoute('/_authenticated/jobs/$jobId/overview')({
  component: JobOverviewTab,
})

function JobOverviewTab() {
  const { t } = useTranslation()
  const { jobId } = useParams({ from: '/_authenticated/jobs/$jobId' })
  const { data: job } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => jobsAPI.get(jobId),
  })

  const j = job as any
  if (!j) return null

  const statusTitle = j.status_title || j.status?.title || 'Unknown'
  const statusColor = j.status_color || j.status?.color || null
  const subStatusTitle = j.sub_status_title || j.subStatus?.title
  const categoryTitle = j.category_title || j.category?.title || '—'
  const subCategoryTitle = j.sub_category_title || j.subCategory?.title
  const customerName = j.customer_name || j.customer?.name || '—'
  const contactName = [j.contact_first_name, j.contact_last_name].filter(Boolean).join(' ') || j.contact?.name || '—'
  const contactPhone = j.contact_phone || j.contact?.phone || null
  const contactEmail = j.contact_email || j.contact?.email || null
  const locationAddress = j.location_address || j.location?.address || '—'
  const locationCity = j.location_city || j.location?.city || ''
  const locationState = j.location_state || j.location?.state || ''
  const locationZip = j.location_zip_code || j.location?.zipCode || ''
  const fullAddress = [locationAddress, locationCity, locationState, locationZip].filter(Boolean).join(', ')

  return (
    <motion.div
      initial={{ opacity: 0, x: 10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.15 }}
      className="space-y-4"
    >
      <div className="grid gap-4 md:grid-cols-2">
        {/* Job Details Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="w-4 h-4" /> Job Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow icon={Hash} label={t('jobDetail:jobNumber')}>
              {j.job_number || j.jobNumber}
              <CopyButton text={j.job_number || j.jobNumber || ''} />
            </InfoRow>
            <InfoRow icon={Tag} label={t('jobDetail:category')}>
              {categoryTitle}
              {subCategoryTitle && <span className="text-muted-foreground"> / {subCategoryTitle}</span>}
            </InfoRow>
            <InfoRow icon={CheckCircle2} label={t('jobDetail:status')}>
              <StatusBadge title={statusTitle} color={statusColor} />
              {subStatusTitle && <StatusBadge title={subStatusTitle} color={j.sub_status_color} />}
            </InfoRow>
            {j.po && <InfoRow icon={FileText} label={t('jobDetail:poNumber')}>{j.po}</InfoRow>}
            {j.sf_id && <InfoRow icon={ExternalLink} label={t('jobDetail:salesforceId')}>{j.sf_id}</InfoRow>}
            <InfoRow icon={CreditCard} label={t('jobDetail:billingCompany')}>
              {j.billing_company_id || '—'}
            </InfoRow>
            <InfoRow icon={Clock} label={t('jobDetail:created')}>{formatDateTime(j.created_at || j.createdAt)}</InfoRow>
            <InfoRow icon={Clock} label={t('jobDetail:updated')}>{formatDateTime(j.updated_at || j.updatedAt)}</InfoRow>
          </CardContent>
        </Card>

        {/* Customer & Contact Card */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users className="w-4 h-4" /> Customer & Contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <InfoRow icon={Building2} label={t('jobDetail:customer')}>{customerName}</InfoRow>
            <InfoRow icon={User} label={t('jobDetail:contact')}>{contactName}</InfoRow>
            {contactPhone && (
              <InfoRow icon={Phone} label={t('jobDetail:phone')}>
                <a href={`tel:${contactPhone}`} className="text-primary hover:underline">{contactPhone}</a>
              </InfoRow>
            )}
            {contactEmail && (
              <InfoRow icon={Mail} label={t('jobDetail:email')}>
                <a href={`mailto:${contactEmail}`} className="text-primary hover:underline">{contactEmail}</a>
              </InfoRow>
            )}
            <Separator className="my-2" />
            <InfoRow icon={MapPin} label={t('jobDetail:serviceLocation')}>
              <span>{fullAddress}</span>
              {locationAddress !== '—' && (
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ml-1.5 text-primary hover:underline inline-flex items-center gap-0.5 text-xs"
                >
                  Map <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </InfoRow>
            {j.invoice_sub_customer && (
              <InfoRow icon={AlertTriangle} label={t('jobDetail:invoiceSubcustomer')}>
                <Badge variant="outline" className="text-amber-600">{t('jobDetail:yes')}</Badge>
              </InfoRow>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Description */}
      {(j.description || j.completion_notes) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> Description
            </CardTitle>
          </CardHeader>
          <CardContent>
            {j.description && (
              <p className="text-sm whitespace-pre-wrap">{j.description}</p>
            )}
            {j.completion_notes && (
              <div className="mt-3 p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                <p className="text-xs font-medium text-green-700 dark:text-green-400 mb-1">{t('jobDetail:completionNotes')}</p>
                <p className="text-sm whitespace-pre-wrap">{j.completion_notes}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Flags */}
      {(j.two_tech || j.notify_parts_received || j.lead_id) && (
        <div className="flex flex-wrap gap-2">
          {j.two_tech && <Badge variant="outline" className="text-amber-600 border-amber-300"><Users className="w-3 h-3 mr-1" /> 2-Tech Required</Badge>}
          {j.notify_parts_received && <Badge variant="outline" className="text-blue-600 border-blue-300"><Wrench className="w-3 h-3 mr-1" />{t('jobDetail:notifyPartsReceived')}</Badge>}
          {j.lead_id && <Badge variant="outline" className="text-purple-600 border-purple-300"><ChevronRight className="w-3 h-3 mr-1" />{t('jobDetail:fromLead')}</Badge>}
        </div>
      )}
    </motion.div>
  )
}
